import { useEffect } from 'react'
import { Outlet } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { Loader } from './Loader'
import { useBootstrap } from '@/features/ksec/useBootstrap'
import { getAuth0AuthorizationParams } from '@/lib/auth0Config'

/**
 * Protects all child routes (via Outlet).
 *
 * Flow:
 *  1. While Auth0 is initializing → shows a loading screen.
 *  2. If not authenticated → triggers Auth0 Universal Login redirect
 *     (Authorization Code + PKCE, con audience de la API).
 *  3. Once authenticated → runs KSEC bootstrap (parallel: /me/context,
 *     /roles, /auth/effective-access) to populate the global store.
 *  4. Once bootstrap is complete → renders protected content.
 */
export function AuthGuard() {
  const { isLoading, isAuthenticated, loginWithRedirect, getAccessTokenSilently, getIdTokenClaims, user, error } =
    useAuth0()
  const { isBootstrapped, bootstrap } = useBootstrap()

  useEffect(() => {
    if (isLoading || error) return
    if (!isAuthenticated) {
      void loginWithRedirect({
        appState: { returnTo: '/' },
        authorizationParams: getAuth0AuthorizationParams(),
      })
      return
    }
    // Cast: Auth0 user object keys are dynamic (namespaced custom claims)
    void bootstrap(getAccessTokenSilently, getIdTokenClaims, user as Record<string, unknown>)
  }, [isLoading, isAuthenticated, error, bootstrap, getAccessTokenSilently, getIdTokenClaims, loginWithRedirect, user])

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg-secondary">
        <Loader message="Verificando sesión..." />
      </div>
    )
  }

  if (!isAuthenticated) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg-secondary">
        <Loader message="Redirigiendo al inicio de sesión..." />
      </div>
    )
  }

  if (!isBootstrapped) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg-secondary">
        <Loader message="Cargando portal..." />
      </div>
    )
  }

  return <Outlet />
}
